import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import { FiClock, FiGlobe, FiUserPlus, FiCheck, FiPlus, FiTrash2, FiSettings, FiEdit3, FiFlag, FiX, FiMessageCircle, FiLock, FiEyeOff, FiUsers, FiShield, FiSlash } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    users, 
    currentUser, 
    getOrCreateChat, 
    isRequestPending, 
    hasReceivedRequest, 
    isFriend, 
    isBlocked,
    acceptRequest, 
    rejectRequest, 
    sendRequest, 
    unfriend,
    blockPlayer,
    unblockPlayer,
    addGame, 
    removeGame, 
    updateUserGame, 
    globalGames, 
    globalOptions 
  } = useAppContext();
  
  const [gameModal, setGameModal] = useState({ show: false, editMode: false, data: null });

  const user = users.find(u => u.id === id) || currentUser;
  const isMe = user.id === currentUser.id;
  const isPending = isRequestPending(user.id);
  const hasReceived = hasReceivedRequest(user.id);
  const friends = isFriend(user.id);
  const blocked = isBlocked(user.id);
  const isAdmin = user.role === 'admin';

  const privacy = user.privacySettings || {
    showRealName: true,
    showNationality: true,
    showTimezone: true,
    showBio: true,
    showLanguages: true
  };

  const handleGoToChat = () => {
    if (!friends) return;
    const chatId = getOrCreateChat(user.id);
    if (chatId) navigate(`/messages/${chatId}`);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 pb-24 transition-colors">
      <div className="bg-white dark:bg-dark-surface rounded-[32px] p-8 shadow-xl shadow-brand-900/5 border border-white dark:border-dark-border mb-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-50 dark:bg-brand-900/10 rounded-full -mr-16 -mt-16 blur-3xl opacity-50"></div>
        <div className="flex flex-col md:flex-row gap-8 items-center md:items-start relative z-10">
          <div className="relative">
            <img 
              src={user.avatar} 
              alt={user.nickname} 
              className="w-32 h-32 rounded-[2.5rem] object-cover border-4 border-brand-50 dark:border-dark-card shadow-lg" 
            />
            {!isMe && (
              <span className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-4 border-white dark:border-dark-surface ${
                user.isOnline ? 'bg-green-500' : 'bg-gray-300'
              }`}></span>
            )}
          </div>

          <div className="flex-1 text-center md:text-left">
            <div className="flex flex-col md:flex-row items-center gap-3 mb-2">
              <h1 className="text-4xl font-black text-brand-950 dark:text-white tracking-tight leading-none">{user.nickname}</h1>
              <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ${
                isAdmin 
                  ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-900/30' 
                  : 'bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/20'
              }`}>
                <SafeIcon icon={isAdmin ? FiShield : FiUsers} className="w-3 h-3" />
                {isAdmin ? 'Admin' : 'Player'}
              </span>
            </div>
            
            {(isMe || privacy.showRealName) ? (
              <p className="text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest text-xs mb-6">{user.name}</p>
            ) : (
              <p className="text-gray-300 dark:text-gray-600 font-bold uppercase tracking-widest text-[9px] mb-6 italic flex items-center justify-center md:justify-start gap-1">
                <SafeIcon icon={FiLock} className="w-2.5 h-2.5" /> Private Identity
              </p>
            )}

            <div className="flex flex-wrap justify-center md:justify-start gap-3">
              {(isMe || privacy.showNationality) && user.nationality && (
                <Badge icon={FiFlag} text={user.nationality} />
              )}
              <Badge icon={FiClock} text={user.timezone} />
              {(isMe || privacy.showLanguages) && user.languages?.length > 0 && (
                <div className="flex gap-1.5">
                  {user.languages.map(lang => (
                    <Badge key={lang} icon={FiGlobe} text={lang} variant="brand" />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full md:w-auto min-w-[180px]">
            {isMe ? (
              <button 
                onClick={() => navigate('/profile/edit')}
                className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest bg-gray-50 dark:bg-dark-card text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-border transition-all border border-gray-100 dark:border-dark-border"
              >
                <SafeIcon icon={FiSettings} /> Settings
              </button>
            ) : blocked ? (
              <button 
                onClick={() => unblockPlayer(user.id)}
                className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest bg-emerald-600 text-white shadow-lg hover:bg-emerald-700 transition-all"
              >
                <SafeIcon icon={FiCheck} /> Unblock Player
              </button>
            ) : (
              <>
                {hasReceived ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button 
                      onClick={() => acceptRequest(user.id)}
                      className="flex items-center justify-center gap-2 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest bg-brand-600 text-white shadow-lg shadow-brand-200 dark:shadow-brand-900/20 hover:bg-brand-700 transition-all"
                    >
                      <SafeIcon icon={FiCheck} /> Accept
                    </button>
                    <button 
                      onClick={() => rejectRequest(user.id)}
                      className="flex items-center justify-center gap-2 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest bg-gray-50 dark:bg-dark-card text-gray-400 hover:text-red-500 border border-gray-100 dark:border-dark-border transition-all"
                    >
                      <SafeIcon icon={FiX} /> No
                    </button>
                  </div>
                ) : friends ? (
                  <button 
                    onClick={() => unfriend(user.id)}
                    className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest bg-gray-50 dark:bg-dark-card text-red-500 border border-red-100 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all"
                  >
                    <SafeIcon icon={FiTrash2} /> Unfriend
                  </button>
                ) : (
                  <button 
                    onClick={() => sendRequest(user.id)}
                    disabled={isPending}
                    className={`flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${
                      isPending 
                        ? 'bg-gray-100 dark:bg-dark-card text-gray-400 dark:text-gray-600' 
                        : 'bg-brand-600 text-white shadow-lg shadow-brand-200 dark:shadow-brand-900/20 hover:bg-brand-700'
                    }`}
                  >
                    {isPending ? <><SafeIcon icon={FiCheck} /> Sent</> : <><SafeIcon icon={FiUserPlus} /> Invite</>}
                  </button>
                )}
                
                <button 
                  onClick={handleGoToChat}
                  disabled={!friends}
                  className={`flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${
                    friends 
                      ? 'bg-white dark:bg-dark-surface text-brand-600 dark:text-brand-400 border-2 border-brand-600 dark:border-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/10' 
                      : 'bg-gray-50 dark:bg-dark-card text-gray-300 dark:text-gray-700 border border-gray-100 dark:border-dark-border cursor-not-allowed'
                  }`}
                >
                  <SafeIcon icon={friends ? FiMessageCircle : FiLock} />
                  {friends ? 'Message' : 'Chat Locked'}
                </button>

                <button 
                  onClick={() => blockPlayer(user.id)}
                  className="flex items-center justify-center gap-2 px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest text-gray-400 hover:text-red-600 transition-colors"
                >
                  <SafeIcon icon={FiSlash} /> Block Player
                </button>
              </>
            )}
          </div>
        </div>

        {(isMe || privacy.showBio) ? (
          user.bio && (
            <div className="mt-8 pt-8 border-t border-gray-50 dark:border-dark-border">
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed font-medium italic">"{user.bio}"</p>
            </div>
          )
        ) : (
          <div className="mt-8 pt-8 border-t border-gray-50 dark:border-dark-border flex items-center gap-3 text-gray-300 dark:text-dark-muted">
            <SafeIcon icon={FiEyeOff} className="w-4 h-4" />
            <p className="text-[10px] font-bold uppercase tracking-widest">Bio is private</p>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mb-6 px-2">
        <h2 className="text-2xl font-black text-brand-950 dark:text-white tracking-tight">Game Library</h2>
        {isMe && (
          <button 
            onClick={() => setGameModal({ show: true, editMode: false, data: null })}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg hover:bg-brand-700 transition-all"
          >
            <SafeIcon icon={FiPlus} /> Add New
          </button>
        )}
      </div>

      <div className="grid gap-4">
        <AnimatePresence>
          {user.games.map((game) => (
            <motion.div 
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              key={game.name} 
              className="bg-white dark:bg-dark-surface p-6 rounded-[24px] border border-gray-100 dark:border-dark-border shadow-sm flex items-center justify-between group hover:border-brand-200 dark:hover:border-brand-500/50 transition-all"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-lg font-black text-brand-950 dark:text-white">{game.name}</h3>
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${
                    game.type === 'Competitive' 
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900/30' 
                      : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900/30'
                  }`}>
                    {game.type}
                  </span>
                  {game.lookingForTeam && (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-full border border-amber-100 dark:border-amber-900/30">
                      <SafeIcon icon={FiUsers} className="w-3 h-3" />
                      <span className="text-[9px] font-black uppercase tracking-widest">Recruiting</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {game.roles.map(role => (
                    <span key={role} className="text-[10px] font-bold bg-gray-50 dark:bg-dark-card text-gray-500 dark:text-gray-400 px-2.5 py-1.5 rounded-xl border border-gray-100 dark:border-dark-border">{role}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <span className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-1.5">Level</span>
                  <span className="font-black text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 px-4 py-2 rounded-xl border border-brand-100 dark:border-brand-900/30 shadow-sm">{game.rank}</span>
                </div>
                {isMe && (
                  <div className="flex flex-col gap-1 sm:opacity-0 group-hover:opacity-100 transition-all">
                    <button 
                      onClick={() => setGameModal({ show: true, editMode: true, data: game })}
                      className="p-2.5 text-brand-400 hover:bg-brand-50 dark:hover:bg-dark-card rounded-xl transition-colors"
                    ><SafeIcon icon={FiEdit3} /></button>
                    <button 
                      onClick={() => removeGame(game.name)}
                      className="p-2.5 text-red-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                    ><SafeIcon icon={FiTrash2} /></button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {gameModal.show && (
          <UserGameModal 
            editData={gameModal.data}
            globalGames={globalGames}
            globalOptions={globalOptions}
            onClose={() => setGameModal({ show: false, editMode: false, data: null })}
            onSave={(game) => {
              if (gameModal.editMode) updateUserGame(gameModal.data.name, game);
              else addGame(game);
              setGameModal({ show: false, editMode: false, data: null });
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function UserGameModal({ editData, globalGames, globalOptions, onClose, onSave }) {
  const [name, setName] = useState(editData?.name || Object.keys(globalGames)[0]);
  const [rank, setRank] = useState(editData?.rank || '');
  const [type, setType] = useState(editData?.type || 'Competitive');
  const [platform, setPlatform] = useState(editData?.platform || 'PC');
  const [selectedRoles, setSelectedRoles] = useState(editData?.roles || []);
  const [lookingForTeam, setLookingForTeam] = useState(editData?.lookingForTeam || false);

  const gameInfo = globalGames[name];

  const toggleRole = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-brand-950/60 backdrop-blur-sm" 
      />
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }} 
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-white dark:bg-dark-surface w-full max-w-md rounded-[32px] p-8 shadow-2xl border border-white/20"
      >
        <h3 className="text-2xl font-black text-brand-950 dark:text-white mb-6 uppercase tracking-tight">{editData ? 'Edit Game' : 'Add Game'}</h3>
        
        <div className="space-y-5">
          <div className="mb-2">
            <button 
              type="button"
              onClick={() => setLookingForTeam(!lookingForTeam)}
              className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between group ${
                lookingForTeam 
                  ? 'bg-brand-600 border-brand-600 text-white shadow-lg shadow-brand-200 dark:shadow-brand-900/20' 
                  : 'bg-gray-50 dark:bg-dark-card border-gray-100 dark:border-dark-border text-gray-500 hover:border-brand-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                  lookingForTeam ? 'bg-white/20' : 'bg-white dark:bg-dark-surface border border-gray-100 dark:border-dark-border'
                }`}>
                  <SafeIcon icon={FiUsers} className={lookingForTeam ? 'text-white' : 'text-brand-600'} />
                </div>
                <div className="text-left">
                  <p className={`text-xs font-black uppercase tracking-tight ${lookingForTeam ? 'text-white' : 'text-brand-950 dark:text-white'}`}>Looking for Team</p>
                  <p className={`text-[9px] font-bold uppercase tracking-tight ${lookingForTeam ? 'text-white/70' : 'text-gray-400'}`}>Recruiting for {name}</p>
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${lookingForTeam ? 'border-white bg-white' : 'border-gray-200 dark:border-dark-border'}`}>
                {lookingForTeam && <SafeIcon icon={FiCheck} className="text-brand-600 w-3 h-3" />}
              </div>
            </button>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-2 px-1">Select Game</label>
            <select 
              disabled={!!editData}
              className="w-full bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl p-4 font-bold text-gray-700 dark:text-gray-200 outline-none disabled:opacity-50 appearance-none"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSelectedRoles([]);
                setRank('');
              }}
            >
              {Object.keys(globalGames).map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-2 px-1">Current Rank</label>
              <select 
                className="w-full bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl p-4 font-bold text-gray-700 dark:text-gray-200 outline-none appearance-none"
                value={rank}
                onChange={(e) => setRank(e.target.value)}
              >
                <option value="">Choose...</option>
                {gameInfo.ranks.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-2 px-1">Platform</label>
              <select 
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl p-4 font-bold text-gray-700 dark:text-gray-200 outline-none appearance-none"
              >
                {globalOptions.platforms.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-2 px-1">Approach Type</label>
            <div className="flex gap-2">
              {globalOptions.playerTypes.map(t => (
                <button 
                  key={t} 
                  onClick={() => setType(t)}
                  className={`flex-1 py-3.5 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                    type === t 
                      ? 'bg-brand-600 text-white border-brand-600 shadow-lg' 
                      : 'bg-gray-50 dark:bg-dark-card text-gray-400 dark:text-gray-500 border-gray-100 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-border'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-2 px-1">Your Roles</label>
            <div className="flex flex-wrap gap-2">
              {gameInfo.roles.map(role => (
                <button 
                  key={role} 
                  onClick={() => toggleRole(role)}
                  className={`px-4 py-2 rounded-xl border text-[11px] font-bold transition-all ${
                    selectedRoles.includes(role) 
                      ? 'bg-brand-600 text-white border-brand-600 shadow-md' 
                      : 'bg-gray-50 dark:bg-dark-card text-gray-400 dark:text-gray-500 border-gray-100 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-border'
                  }`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 py-4 font-black text-xs uppercase tracking-widest text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-gray-300 transition-colors">Cancel</button>
            <button 
              onClick={() => onSave({ name, rank, roles: selectedRoles, type, platform, lookingForTeam })}
              disabled={!rank || selectedRoles.length === 0}
              className="flex-2 py-4 bg-brand-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-200 dark:shadow-brand-900/20 disabled:opacity-50"
            >
              Save Configuration
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Badge({ icon, text, variant = 'gray' }) {
  const styles = {
    gray: 'bg-gray-50 dark:bg-dark-card text-gray-500 dark:text-gray-400 border-gray-100 dark:border-dark-border',
    brand: 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 border-brand-100 dark:border-brand-900/30 font-bold'
  };

  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-[11px] font-medium ${styles[variant]}`}>
      {icon && <SafeIcon icon={icon} className="w-3.5 h-3.5" />}
      {text}
    </div>
  );
}